import { makeAutoObservable } from 'mobx';

import type { AssistantTurn } from '../components/solution-assistant/solution-assistant-model';
import { agentSettingsStore } from './agent-settings-store';

export type AssistantTab = 'ask' | 'agents';

/** An Agents-panel tab to open on (see `openAgents`). */
export type AgentsPanelTab =
  | 'configuration'
  | 'supervisor-agent'
  | 'knowledge-agent'
  | 'tasks';

/**
 * Ask AI's transcripts are kept in this browser, one per solution version, so
 * a reload or another visit picks the conversation back up. Only the newest
 * turns are kept. Storage can be missing or full, and then the transcript
 * lives for the page only, as before. Saving them on the server, shared with
 * the team, is backend work that follows.
 */
const TURNS_KEY = 'pro-studio:ask-ai:';
const KEPT_TURNS = 100;

/** Read once per solution version, so a render gets the same list each time. */
const loaded = new Map<string, AssistantTurn[]>();

const loadTurns = (solutionKey: string): AssistantTurn[] => {
  if (!solutionKey) return [];
  const known = loaded.get(solutionKey);
  if (known) return known;
  let turns: AssistantTurn[] = [];
  try {
    const raw = window.localStorage.getItem(TURNS_KEY + solutionKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) turns = parsed as AssistantTurn[];
  } catch {
    // Nothing kept, or storage is unavailable: a new conversation.
  }
  loaded.set(solutionKey, turns);
  return turns;
};

const saveTurns = (solutionKey: string, turns: AssistantTurn[]) => {
  if (!solutionKey) return;
  loaded.set(solutionKey, turns);
  try {
    if (turns.length) {
      window.localStorage.setItem(
        TURNS_KEY + solutionKey,
        JSON.stringify(turns.slice(-KEPT_TURNS))
      );
    } else {
      window.localStorage.removeItem(TURNS_KEY + solutionKey);
    }
  } catch {
    // Not kept past this page; the conversation itself still works.
  }
};

/**
 * The header's AI dock (behind the master `bobcat-five-stage-nav`): whether it is open,
 * which tab ("Ask AI" or "Agents"), and one transcript per solution version,
 * kept while the designer moves between stages. The header toggles it; the
 * Workflows page renders it.
 */
export class SolutionAssistantStore {
  open = false;

  tab: AssistantTab = 'ask';

  /** The Agents panel tab to open on next; consumed when it renders. */
  agentsPanelTab: AgentsPanelTab | null = null;

  /** A node cleared from the context stays cleared until another is selected. */
  clearedRowId: string | null = null;

  private transcripts = new Map<string, AssistantTurn[]>();

  constructor() {
    makeAutoObservable(this);
  }

  toggle() {
    this.open = !this.open;
  }

  openDock() {
    this.openTab('ask');
    this.clearedRowId = null;
  }

  openTab(tab: AssistantTab) {
    this.tab = tab;
    this.open = true;
    this.agentsPanelTab = null;
  }

  /** Opens the dock on Agents, at `panelTab` (e.g. the Knowledge Agent). */
  openAgents(panelTab?: AgentsPanelTab) {
    this.openTab('agents');
    this.agentsPanelTab = panelTab ?? null;
    agentSettingsStore.setTab(
      panelTab === 'knowledge-agent' ? 'knowledge' : 'supervisor'
    );
  }

  setTab(tab: AssistantTab) {
    this.tab = tab;
  }

  close() {
    this.open = false;
  }

  clearNode(rowId: string) {
    this.clearedRowId = rowId;
  }

  turnsFor(solutionKey: string): AssistantTurn[] {
    return this.transcripts.get(solutionKey) ?? loadTurns(solutionKey);
  }

  setTurns(solutionKey: string, turns: AssistantTurn[]) {
    this.transcripts.set(solutionKey, turns);
    saveTurns(solutionKey, turns);
  }

  /** "New chat": the transcript goes, here and in this browser. */
  clearTurns(solutionKey: string) {
    this.setTurns(solutionKey, []);
  }

  /**
   * Adds a turn to the transcript as it is NOW. The dock answers after an
   * await, and a list read before it would drop what changed meanwhile — an
   * Apply recorded while the question was in flight (PSTUDIO-4631).
   */
  appendTurn(solutionKey: string, turn: AssistantTurn) {
    this.setTurns(solutionKey, [...this.turnsFor(solutionKey), turn]);
  }

  /** Rewrites turn `i` from its current value; see `appendTurn`. */
  updateTurn(
    solutionKey: string,
    i: number,
    update: (turn: AssistantTurn) => AssistantTurn
  ) {
    this.setTurns(
      solutionKey,
      this.turnsFor(solutionKey).map((turn, j) =>
        j === i ? update(turn) : turn
      )
    );
  }
}

export const solutionAssistantStore = new SolutionAssistantStore();
